/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import { WorkshopCard } from '../ui/WorkshopCard';
import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getWorkshops } from '@/sanity/client';

export const Workshops = () => {
	const [workshops, setWorkshops] = useState<any[]>([]);
	const [activeSlide, setActiveSlide] = useState(0);
	const [loading, setLoading] = useState(true);
	const navigate = useNavigate();

	useEffect(() => {
		const fetchWorkshops = async () => {
			try {
				const workshopData = await getWorkshops();

				// Format the data with day, month, year fields
				const formattedWorkshops = workshopData.map((workshop) => ({
					...workshop,
					day: workshop.date
						? new Date(workshop.date).getDate().toString()
						: '',
					month: workshop.date
						? new Date(workshop.date).toLocaleString('default', {
								month: 'short',
							})
						: '',
					year: workshop.date
						? new Date(workshop.date).getFullYear().toString()
						: '',
					// Ensure these fields are present or provide defaults
					calendarIconUrl: workshop.calendarIconUrl || '/calendar-icon.svg',
					variant: workshop.variant || 'light',
				}));

				// Sort workshops by date in ascending order
				const sortedWorkshops = [...formattedWorkshops].sort((a, b) => {
					// If either workshop doesn't have a date, put it at the end
					if (!a.date) return 1;
					if (!b.date) return -1;

					// Compare dates
					return new Date(a.date).getTime() - new Date(b.date).getTime();
				});

				setWorkshops(sortedWorkshops);
			} catch (error) {
				console.error('Error fetching workshops:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchWorkshops();
	}, []);

	const nextSlide = () => {
		setActiveSlide((prev) => (prev === workshops.length - 1 ? 0 : prev + 1));
	};

	const handleWorkshopClick = (id: string) => {
		navigate(`/workshops/${id}`);
	};

	if (loading) {
		return (
			<section className='rounded-none'>
				<div className='flex flex-col justify-center items-center px-20 py-24 w-full bg-gray-100 rounded-none max-md:px-5 max-md:max-w-full'>
					<div className='w-full max-w-[1328px] max-md:max-w-full md:px-25'>
						<p>Loading workshops...</p>
					</div>
				</div>
			</section>
		);
	}

	if (workshops.length === 0) {
		return (
			<section className='rounded-none'>
				<div className='flex flex-col justify-center items-center px-20 py-24 w-full bg-gray-100 rounded-none max-md:px-5 max-md:max-w-full'>
					<div className='w-full max-w-[1328px] max-md:max-w-full md:px-25'>
						<p>No upcoming workshops at the moment.</p>
					</div>
				</div>
			</section>
		);
	}

	return (
		<section className='rounded-none'>
			<div className='flex flex-col justify-center items-center px-20 py-24 w-full bg-gray-100 rounded-none max-md:px-5 max-md:max-w-full'>
				<div className='w-full max-w-[1328px] max-md:max-w-full md:px-25'>
					<header className='max-w-full text-neutral-800 w-[427px]'>
						<h1 className='text-4xl font-bold leading-[40px] max-md:text-[30px] max-md:leading-[40px]'>
							Be part of <br />
							the experience.
						</h1>
						<h2 className='mt-6 text-2xl leading-snug'>Upcoming workshops.</h2>
					</header>

					{/* Desktop view - Grid */}
					<div className='hidden md:block mt-16'>
						<div className='flex gap-5 w-full'>
							{workshops.map((event, index) => (
								<div key={index} className='w-4/12'>
									<WorkshopCard
										day={event.day}
										month={event.month}
										year={event.year}
										title={event.title}
										time={event.time}
										location={event.location}
										variant={event.variant as 'dark' | 'light'}
										price={event.price}
										limit={event.limit}
										onClick={() => handleWorkshopClick(event.id)}
									/>
								</div>
							))}
						</div>
					</div>

					{/* Mobile view - Carousel */}
					<div className='md:hidden w-full mt-10 overflow-hidden relative'>
						<div
							className='flex transition-all duration-500 ease-in-out'
							style={{ transform: `translateX(-${activeSlide * 100}%)` }}
						>
							{workshops.map((event, index) => (
								<div key={index} className='w-full flex-shrink-0 px-2.5'>
									<WorkshopCard
										day={event.day}
										month={event.month}
										year={event.year}
										title={event.title}
										time={event.time}
										location={event.location}
										variant={event.variant as 'dark' | 'light'}
										price={event.price}
										limit={event.limit}
										onClick={() => handleWorkshopClick(event.id)}
									/>
								</div>
							))}
						</div>

						{/* Carousel controls */}
						<div className='flex w-full items-center gap-5 justify-center mt-8'>
							<div className='flex items-center gap-2.5 my-auto p-2'>
								{workshops.map((_, index) => (
									<button
										key={index}
										onClick={() => setActiveSlide(index)}
										className='focus:outline-none'
										aria-label='Workshop slide'
									>
										<div
											className={`${
												activeSlide === index
													? 'bg-[rgba(251,176,64,1)] h-4 w-4'
													: 'bg-[rgba(251,176,64,0.5)] h-3 w-3'
											} rounded-full transition-all duration-300`}
										/>
									</button>
								))}
							</div>
							<button
								onClick={nextSlide}
								className='flex items-center justify-center bg-[rgba(251,176,64,1)] rounded-full w-12 h-12 text-white transition-transform hover:scale-105 ml-4'
								aria-label='Next workshop'
							>
								<ChevronRight className='h-6 w-6' />
							</button>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};
